import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Rect } from 'react-native-svg';
import { MaterialCommunityIcons } from '../components/icons';
import { BrandLogo } from '../components/BrandLogo';
import { CONTENU_BORNE } from '../navigation/screenOptions';
import { FondAube } from '../components/FondAube';
import { useReduceMotion } from '../hooks/useReduceMotion';
import { GradientText } from '../components/GradientText';
import { t } from '../i18n';
import { useGabarit } from '../hooks/useGabarit';
import { CarriereLarge } from './CarriereLarge';
import { FEATURE_ACCENTS } from '../theme/featureAccents';
import {
  SUR_LE_WEB,
  colors,
  gradients,
  layout,
  radius,
  screenPadding,
  shadow,
  spacing,
  typography,
} from '../theme/tokens';
import { BLOC_LTR, PROPS_LTR, interfaceRtl, styleMiroir } from '../i18n/sens';

export interface CarriereScreenProps {
  onOpenMyCv: () => void;
  onOpenLinkedin: () => void;
  onOpenLetter: () => void;
  onOpenInterview: () => void;
}

interface Entry {
  key: string;
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  title: string;
  description: string;
  /** Verbe PROPRE à la feature : il dit ce que le tap déclenche, pas qu'il ouvre quelque chose. */
  cta: string;
  /** Couleur charte de l'entité : ici c'est le FOND de la carte, donc son identité entière. */
  color: string;
  onPress: () => void;
  testID: string;
}

/** Épaisseur des traits de progression sous le carrousel. */
const ACCENT_HEIGHT = 4;

/**
 * PART DE L'ÉCRAN LAISSÉE À CHAQUE VOISINE, de chaque côté. EN PROPORTION et non en points : c'est
 * la fraction de voisine visible qui doit rester constante d'un appareil à l'autre, pas un nombre
 * de points qui donnerait un liseré sur Pro Max et une demi-carte sur SE.
 *
 * C'est cette valeur, et elle seule, qui fixe la largeur des cartes : `largeur × (1 − 2 × ratio)`.
 * À 0,21 on voit un peu moins de la moitié de chaque voisine, ce que montre la maquette.
 */
const CARD_PEEK_RATIO = 0.21;

/**
 * RECOUVREMENT, et non espacement : les cartes se chevauchent légèrement, la voisine passant SOUS
 * l'active (voir le `zIndex` plus bas). Un espacement positif les poserait côte à côte, à plat, et
 * la profondeur que l'échelle installe se perdrait dans l'écart.
 */
const CARD_OVERLAP = spacing.xl;

/**
 * HAUTEUR DE CARTE EN PROPORTION DE L'ÉCRAN, et non en multiples d'espacement.
 *
 * La carte est le sujet de l'écran : elle doit occuper la même part du regard sur un SE que sur un
 * Pro Max. Une hauteur en points fixes donnerait une carte dominante sur le petit écran et un timbre
 * perdu sur le grand. C'est un PLANCHER (`minHeight`) : un texte long fait grandir la carte au lieu
 * d'être tronqué.
 */
const CARD_HEIGHT_RATIO = 0.46;

/**
 * GÉOMÉTRIE DU RAIL — proportionnelle tant que la fenêtre est étroite, fixe au-delà d'un seuil.
 *
 * POURQUOI LE PROPORTIONNEL NE PEUT PAS CONTINUER. `largeur × 0,58` est juste sur un téléphone :
 * d'un SE à un Pro Max, la carte garde la même part du regard. Sur une fenêtre de 2000 px, la même
 * règle donne une carte de 1160 px — elle occupe presque tout, ses voisines sortent du cadre, et
 * le carrousel cesse d'être un carrousel. Au-delà du seuil, la carte prend donc une largeur EN
 * PIXELS et c'est la marge qui absorbe le reste.
 *
 * `dispo` EST LA LARGEUR DE LA FENÊTRE, sans plafond — voir la note dans le corps : l'écran
 * Carrière est pleine largeur sur le web, le rail doit courir jusqu'aux deux bords.
 *
 * `sideInset` est ÉCRIT SOUS SA FORME GÉNÉRALE, `(dispo − carte) / 2`, au lieu de `dispo × ratio`.
 * Les deux sont algébriquement identiques quand la carte est proportionnelle — `dispo − dispo ×
 * (1 − 2r) = dispo × 2r` — mais seule la première reste vraie quand la carte devient fixe. C'est
 * elle qui centre la carte active, à toute largeur.
 *
 * Aucune de ces bornes n'est atteignable sur mobile : `orientation: portrait` et
 * `supportsTablet: false` plafonnent la largeur à 430 pt, très en dessous du seuil de 700. En
 * dessous du seuil, cette fonction rend exactement ce que rendait le calcul d'avant.
 */
export function geometrieCarrousel(width: number, height: number) {
  /**
   * TOUTE LA LARGEUR DE LA FENÊTRE, sans plafond.
   *
   * Ce calcul plafonnait auparavant à `contentMaxWidth`, parce que le rail vivait dans le conteneur
   * borné. Depuis que l'écran Carrière est pleine largeur sur le web (cf. `CvNavigator`), le
   * plafond produisait exactement le défaut qu'il devait éviter : le rail s'arrêtait à 1200 px, la
   * carte suivante était tranchée à la verticale, et au-delà il ne restait que le fond.
   *
   * Sur mobile, rien ne change : `Math.min(largeur, 1200)` valait déjà la largeur elle-même en
   * dessous de 1200, et un téléphone plafonne à 430.
   */
  const dispo = width;
  const proportionnel = width < layout.carouselFixedFrom;
  const cardWidth = proportionnel ? dispo * (1 - CARD_PEEK_RATIO * 2) : layout.carouselCardWidth;

  /**
   * DEMI-MARGE, ET DEUX ÉCRITURES POUR UNE SEULE QUANTITÉ — délibérément.
   *
   * `dispo × ratio` et `(dispo − carte) / 2` sont algébriquement égales quand la carte est
   * proportionnelle. Elles ne le sont PAS en virgule flottante : sur un SE, la première rend
   * 78,75 et la seconde 78,749999999999986. Un quatorzième de milliardième de point ne se voit pas,
   * mais il fait échouer l'égalité stricte du test de dimensionnement — et surtout, « le mobile ne
   * bouge pas » se vérifie au bit près ou ne se vérifie pas. La branche proportionnelle garde donc
   * l'arithmétique EXACTE d'avant ce lot. La forme générale ne sert qu'à la carte fixe, où le
   * ratio ne veut plus rien dire.
   */
  const demiMarge = proportionnel ? dispo * CARD_PEEK_RATIO : (dispo - cardWidth) / 2;

  return {
    cardWidth,
    cardHeight: Math.round(height * CARD_HEIGHT_RATIO),
    // Le pas SOUSTRAIT le recouvrement : deux cartes consécutives avancent d'une largeur moins ce
    // qu'elles partagent.
    snap: cardWidth - CARD_OVERLAP,
    // Compense la demi-marge négative de la première carte, sans quoi tout le rail serait décalé
    // d'un demi-recouvrement vers la gauche et aucune carte ne serait jamais centrée.
    sideInset: demiMarge + CARD_OVERLAP / 2,
  };
}

/**
 * MISE EN RETRAIT DES VOISINES. Les deux vont ensemble : l'échelle dit « c'est plus loin », l'opacité
 * dit « ce n'est pas ce que tu lis ». L'une sans l'autre donne soit un empilement plat, soit un
 * texte gris qu'on essaie quand même de déchiffrer.
 */
const NEIGHBOUR_SCALE = 0.8;
const NEIGHBOUR_OPACITY = 0.35;

/**
 * INCLINAISON DES VOISINES — effet « coverflow ».
 *
 * `perspective` PUIS `rotateY`, dans cet ordre : la perspective doit être établie avant la rotation,
 * sinon celle-ci s'applique à plat et l'on obtient un simple écrasement horizontal, sans profondeur.
 *
 * QUINZE DEGRÉS AU PLUS. Au-delà, le titre d'une carte voisine devient illisible et l'effet vire au
 * gadget — on n'incline pas pour montrer qu'on sait incliner, mais pour dire « il y a autre chose à
 * côté ». Les deux transformations passent le pilote natif, comme l'échelle et l'opacité : le geste
 * reste suivi image par image, sans repasser l'animation en JS.
 *
 * SIGNE — LE CRITÈRE EST GÉOMÉTRIQUE, PAS UNE CONVENTION D'ANGLE.
 *
 * Ce qu'il faut obtenir se vérifie à l'œil sans rien savoir de `rotateY` : CHAQUE VOISINE EST UN
 * TRAPÈZE DONT LE CÔTÉ COURT TOUCHE LA CARTE CENTRÉE. Son arête intérieure recule, son arête
 * extérieure reste au premier plan.
 *
 * Relevé au pixel sur la maquette, et c'est la référence : carte de gauche, 494 px de haut au bord
 * extérieur contre 477 au bord intérieur ; carte de droite, 495 contre 479. Symétrique, sans
 * ambiguïté.
 *
 * Le premier essai déduisait le signe de la convention CSS (« un angle positif éloigne le bord
 * droit ») et donnait l'inverse — les voisines s'écartaient du centre. NE PAS RÉ-INVERSER SUR LA FOI
 * D'UN RAISONNEMENT : si le doute revient, mesurer les deux hauteurs plutôt que raisonner sur les
 * degrés.
 */
const PERSPECTIVE = 1000;
export const NEIGHBOUR_TILT = '15deg';
const NEIGHBOUR_TILT_INVERSE = '-15deg';

/**
 * DÉFILEMENT CIRCULAIRE : le jeu de cartes est répété TROIS fois et l'on démarre au centre du bloc
 * médian. À la fin de l'élan — jamais pendant le geste, ce qui se verrait comme une saccade — on se
 * repositionne silencieusement d'un bloc si l'on a dérivé vers un bloc latéral.
 *
 * Trois exemplaires suffisent pour quatre cartes : il faut un bloc de réserve de chaque côté, et le
 * repositionnement ramène toujours au médian avant qu'on puisse atteindre un bord.
 */
export const CAROUSEL_REPETITIONS = 3;

/**
 * DÉCISION DE RECENTRAGE, isolée du composant pour être vérifiable.
 *
 * Rend le nouveau décalage, ou `null` s'il n'y a rien à faire. L'INVARIANT qui compte : le
 * déplacement vaut TOUJOURS exactement une largeur de bloc. C'est lui qui garantit que rien ne
 * bouge à l'écran — la carte centrée le reste, et les bornes d'interpolation de sa jumelle sont
 * décalées d'autant. Un déplacement d'une autre amplitude ferait sauter l'image ET fausserait
 * l'inclinaison des voisines, ce qui est le défaut le plus courant de ce montage.
 */
/**
 * BORNES D'INTERPOLATION D'UN TRAIT, sur un rail RÉPÉTÉ.
 *
 * Chaque feature apparaît une fois par bloc : la carte `i` occupe les positions `i`, `i + total`,
 * `i + 2 × total`. Le trait doit donc s'allumer TROIS fois sur la course, pas une.
 *
 * C'est exactement le défaut qui a existé ici : l'état discret était passé au modulo mais pas
 * l'interpolation, restée bornée au premier bloc. Comme le rail DÉMARRE au bloc médian, toutes les
 * bornes étaient déjà franchies à l'ouverture — aucun trait ne s'allumait, et défiler vers la droite
 * ne pouvait rien y changer. Rien n'échouait : les traits étaient simplement tous gris.
 *
 * Les points sont ajoutés en ordre STRICTEMENT croissant, ce qu'`interpolate` exige. Entre deux
 * allumages, la valeur reste au niveau bas — la vallée est plate, pas interpolée.
 */
export function bornesIndicateur(
  index: number,
  snap: number,
  total: number,
  repetitions: number = CAROUSEL_REPETITIONS,
): { points: number[]; centres: Set<number> } {
  const points: number[] = [];
  const centres = new Set<number>();
  for (let bloc = 0; bloc < repetitions; bloc += 1) {
    const centre = (index + bloc * total) * snap;
    centres.add(centre);
    for (const p of [centre - snap, centre, centre + snap]) {
      // Deux blocs trop rapprochés (moins de trois features) feraient se chevaucher les triplets :
      // on n'insère que ce qui progresse, plutôt que de laisser `interpolate` lever.
      if (points.length === 0 || p > points[points.length - 1]) points.push(p);
    }
  }
  return { points, centres };
}

export function recentrer(x: number, largeurBloc: number): number | null {
  if (largeurBloc <= 0) return null;
  if (x < largeurBloc * 0.5) return x + largeurBloc;
  if (x >= largeurBloc * 2.5) return x - largeurBloc;
  return null;
}

/**
 * BLANCS TRANSLUCIDES POSÉS SUR LE FOND COLORÉ.
 *
 * Translucides et non opaques : chaque carte a sa couleur, un blanc opaque calculé pour l'une
 * jurerait sur les trois autres. En laissant transparaître le fond, la même valeur s'accorde
 * partout. Même raisonnement que `colors.violetVeil`, mais l'inverse — du blanc sur de la couleur.
 * Local au fichier : ces deux valeurs n'ont de sens que sur une surface colorée pleine.
 */
const TEXT_ON_COLOR_SOFT = 'rgba(255, 255, 255, 0.82)';

/**
 * HALO derrière le glyphe : la lumière semble venir de DERRIÈRE lui, elle n'est pas un aplat posé
 * dessous. Le dégradé radial s'éteint AVANT le bord de la forme, si bien qu'aucune arête n'est
 * visible — c'est ce qui distingue un halo d'un carré translucide.
 *
 * PAS DE BlurView : un flou restitue ce qu'il y a DERRIÈRE lui, or derrière se trouve un aplat
 * uni. Il ne produirait rien du tout, tout en coûtant une passe de rendu et une dépendance.
 * `react-native-svg` est déjà installé et sert déjà à DecorBlob.
 *
 * Opacité ÉCHANTILLONNÉE : au plus près du glyphe la maquette éclaircit le fond de la carte
 * d'environ 19 à 24 % de blanc, et retombe à zéro sur une distance comparable à la boîte.
 */
const HALO_OPACITY = 0.24;

const GLYPH_BOX = spacing.xxl * 3;
/**
 * LUEUR « LED » SOUS LA CARTE : la couleur de la carte, projetée derrière elle VERS LE BAS SEULEMENT,
 * collée à son bord comme un prolongement, presque un contour.
 *
 * ELLE ÉPOUSE LA FORME DE LA CARTE. Une bande rectangulaire sous la carte se lisait comme un objet
 * posé dessous, avec un bord net et des coins carrés. Ici, des rectangles arrondis de la largeur de la
 * carte, empilés et de plus en plus bas, chacun très translucide : leur superposition donne un flou
 * sans filtre (qui n'existe pas en React Native), identique sur iOS et Android. Les coins s'arrondissent
 * et les flancs rentrent un peu en s'éloignant, si bien que la lueur se resserre naturellement, sans
 * bord ni arête.
 * L'opacité cumulée est maximale contre la carte et tombe à zéro au bout, vite d'abord puis lentement,
 * comme une lumière diffuse. Elle respire très légèrement (voir `respiration`).
 */
const LED_HAUTEUR = spacing.xxl * 2 + spacing.sm;
/** Opacité cumulée contre le bord de la carte. */
const LED_OPACITY = 0.6;
/** Nombre de couches : assez pour qu'aucun palier ne se voie, trop peu et la lueur devient rayée. */
const LED_COUCHES = 28;
/** Profondeur de chaque couche : resserrées près de la carte, espacées au loin (exposant > 1). */
export const LED_PROFONDEURS: readonly number[] = Array.from(
  { length: LED_COUCHES },
  (_, i) => Math.round(LED_HAUTEUR * ((i + 1) / LED_COUCHES) ** 1.6 * 10) / 10,
);
/**
 * Resserrement latéral : une couche profonde rentre de `LED_RETRAIT * profondeur` de chaque côté. À
 * profondeur nulle elle fait exactement la largeur de la carte (la lueur prolonge le bord sans marche),
 * puis elle se resserre doucement, comme un halo qui s'éteint plutôt qu'une bande posée dessous.
 */
const LED_RETRAIT = 0.14;
/** Opacité d'UNE couche, telle que l'empilement donne `LED_OPACITY` contre la carte. */
const LED_COUCHE_OPACITY = 1 - (1 - LED_OPACITY) ** (1 / LED_COUCHES);
/** Plage d'opacité de la respiration, et sa période. */
const LED_RESPIRATION: readonly [number, number] = [0.8, 1];
const LED_RESPIRATION_MS = 3200;

const GLYPH_SIZE = spacing.xxl + spacing.sm;
/** Coins arrondis de la tuile de verre (environ 30 % du côté). */
const GLYPH_RADIUS = spacing.xl + spacing.xs + 2;

/** Les deux étincelles de la maquette : une principale, une plus petite et plus discrète. */
const SPARKLE_MAIN = spacing.lg;
const SPARKLE_SMALL = spacing.md;
const SPARKLE_FAINT_OPACITY = 0.55;

/** Longueur du trait de la carte affichée ; les autres sont comprimés à INDICATOR_IDLE_WIDTH. */
const INDICATOR_ACTIVE_WIDTH = spacing.xxl + spacing.sm;
const INDICATOR_IDLE_WIDTH = spacing.lg;

/**
 * Une carte du carrousel : un BLOC PLEIN de la couleur de la feature.
 *
 * C'est la couleur qui porte l'identité, pas un liseré — d'où le glyphe et les textes en blanc, et
 * le bouton en pastille blanche portant le libellé dans la couleur de la carte. Conséquence à
 * connaître : l'accent de la feature se lit désormais sur `backgroundColor` de la carte, plus sur
 * la couleur du glyphe (voir `featureAccents.test.tsx`, qui compare les couleurs RENDUES).
 *
 * PAS DE `maxFontSizeMultiplier` ICI, volontairement. Le plafond de 1,3× de l'ancien accueil venait
 * de sa grille à deux colonnes, où chaque carte ne disposait que de 114 pt : c'était une conséquence
 * de la largeur, jamais une convention. Une carte de carrousel occupe presque tout l'écran et
 * grandit avec le texte — rien ne justifie de rogner ce que la personne a demandé.
 */
function EntryCard({
  entry,
  index,
  total,
  scrollX,
  snap,
  cardWidth,
  cardHeight,
  active,
  position,
  respiration,
}: {
  entry: Omit<Entry, 'key'>;
  index: number;
  total: number;
  scrollX: Animated.Value;
  snap: number;
  cardWidth: number;
  cardHeight: number;
  active: boolean;
  /** Rang RÉEL dans la série (1 à 4), pas l'index dans la liste triplée. */
  position: number;
  /** Valeur 0 → 1 partagée par toutes les cartes : une seule boucle pour douze lueurs. */
  respiration: Animated.Value;
}) {
  const { icon, title, description, cta, color, onPress, testID } = entry;
  const respirationLed = respiration.interpolate({ inputRange: [0, 1], outputRange: [...LED_RESPIRATION] });

  // Bornes VOISINE / ACTIVE / VOISINE. Hors de cet intervalle l'interpolation est bloquée
  // (`extrapolate: 'clamp'`) : sans cela une carte à deux positions de distance continuerait de
  // rétrécir et finirait par s'inverser.
  const plage = [(index - 1) * snap, index * snap, (index + 1) * snap];
  const scale = scrollX.interpolate({
    inputRange: plage,
    outputRange: [NEIGHBOUR_SCALE, 1, NEIGHBOUR_SCALE],
    extrapolate: 'clamp',
  });
  const opacity = scrollX.interpolate({
    inputRange: plage,
    outputRange: [NEIGHBOUR_OPACITY, 1, NEIGHBOUR_OPACITY],
    extrapolate: 'clamp',
  });
  // À `plage[0]` la carte est UNE POSITION À DROITE du centre : c'est son bord GAUCHE, celui qui
  // touche la carte centrée, qui doit reculer — d'où l'angle négatif en premier. À `plage[2]` elle
  // est à gauche et reçoit l'opposé. Dans les deux cas, le côté court touche le centre.
  const rotateY = scrollX.interpolate({
    inputRange: plage,
    outputRange: [NEIGHBOUR_TILT_INVERSE, '0deg', NEIGHBOUR_TILT],
    extrapolate: 'clamp',
  });

  return (
    /* `zIndex` suit l'ÉTAT et non l'animation : l'ordre d'empilement est discret par nature, il
       bascule au franchissement, exactement là où les deux cartes se croisent. L'interpoler
       n'aurait pas de sens et ne passerait pas au pilote natif. */
    <Animated.View
      testID={`${testID}-slot`}
      style={[
        styles.slot,
        {
          width: cardWidth,
          // ORDRE SIGNIFIANT : perspective, puis rotation, puis échelle.
          transform: [{ perspective: PERSPECTIVE }, { rotateY }, { scale }],
          opacity,
          zIndex: active ? 2 : 1,
        },
      ]}
    >
      {/* Lueur sous la carte, décorative (voir LED_COUCHES). Un identifiant de dégradé PAR CARTE :
          react-native-svg ne cloisonne pas les `id` entre instances. */}
      <Animated.View
        testID={`${testID}-led`}
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={[styles.led, { opacity: respirationLed }]}
      >
        <Svg width={cardWidth} height={LED_HAUTEUR + radius.xl * 2}>
          {LED_PROFONDEURS.map((d, i) => (
            <Rect
              key={i}
              testID={`${testID}-led-couche-${i}`}
              x={d * LED_RETRAIT}
              y={0}
              width={cardWidth - 2 * d * LED_RETRAIT}
              height={radius.xl * 2 + d}
              // Jamais moins arrondi que la carte (la couche est au moins deux rayons de haut), de plus en
              // plus au fil de la descente.
              rx={Math.min(radius.xl + d * 0.6, radius.xl + d / 2)}
              fill={color}
              fillOpacity={LED_COUCHE_OPACITY}
            />
          ))}
        </Svg>
      </Animated.View>
      <Pressable
        testID={testID}
        accessibilityRole="button"
        // UN SEUL ÉLÉMENT ACCESSIBLE par carte. Le bouton visuel n'est pas un `Pressable` : il
        // ferait un second arrêt VoiceOver menant exactement au même endroit. Le libellé du bouton
        // entre donc DANS cette annonce — c'est lui qui dit ce que le tap déclenche. La position
        // dans la série ferme l'annonce, sinon rien ne dirait qu'il reste des cartes à côté.
        accessible
        // POSITION RÉELLE, jamais l'index de la liste triplée : « Carte 1 sur 4 », jamais
        // « Carte 5 sur 12 ». La duplication est un artifice d'affichage, elle n'a pas à s'entendre.
        accessibilityLabel={`${title}. ${description} ${cta}. ${t('carriere.position', {
          index: position,
          total,
        })}`}
        onPress={onPress}
        // `elevation` double le `zIndex` pour Android, où c'est elle qui ordonne les frères.
        style={[
          styles.card,
          { backgroundColor: color, minHeight: cardHeight, elevation: active ? 12 : 2 },
        ]}
      >
        <View style={[styles.body, interfaceRtl() && styles.corpsRtl]}>
          <View style={styles.glyphBox}>
            {/* TUILE DE VERRE : un voile blanc plus dense en haut à gauche, un liseré clair et une ombre
                douce. Elle remplace le halo flou — la forme est nette, elle porte le glyphe. */}
            <LinearGradient
              testID={`${testID}-halo`}
              colors={['rgba(255,255,255,0.30)', 'rgba(255,255,255,0.08)']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.verre}
              pointerEvents="none"
            />

            {/* Le testID reste, il est l'ancre de plusieurs tests. Sa COULEUR n'est plus l'accent
                de la feature : sur un fond plein, le glyphe est blanc. */}
            <MaterialCommunityIcons
              testID={`${testID}-icon`}
              name={icon}
              size={GLYPH_SIZE}
              color={colors.white}
            />

            {/* DÉCORATIF, donc retiré de l'arbre d'accessibilité des deux côtés. La carte est déjà
                un élément unique (`accessible`), mais le déclarer ici évite qu'un remaniement
                futur laisse « étoile à quatre branches » entrer dans l'annonce. */}
            <View
              testID={`${testID}-sparkles`}
              style={styles.sparkles}
              pointerEvents="none"
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
            >
              <MaterialCommunityIcons
                testID={`${testID}-sparkle-main`}
                name="star-four-points"
                size={SPARKLE_MAIN}
                color={colors.white}
                style={styles.sparkleMain}
              />
              <MaterialCommunityIcons
                testID={`${testID}-sparkle-small`}
                name="star-four-points"
                size={SPARKLE_SMALL}
                color={colors.white}
                style={styles.sparkleSmall}
              />
            </View>
          </View>

          <Text style={styles.cardTitle}>{title}</Text>
          <Text style={styles.cardDescription}>{description}</Text>
        </View>

        <View testID={`${testID}-cta`} style={styles.cta}>
          <Text style={[styles.ctaText, { color }]}>{cta}</Text>
          <View style={styleMiroir()}><MaterialCommunityIcons name="arrow-right" size={typography.bodyMd.size} color={color} /></View>
        </View>
      </Pressable>
    </Animated.View>
  );
}

/**
 * Onglet « Carrière » — SOMMAIRE des features, rien de plus.
 *
 * Chaque carte pousse son écran ; aucun contenu de feature n'est déplié ici. C'est ce qui permet
 * d'accueillir une cinquième entrée avec un objet du tableau et une route, sans refonte : le
 * carrousel et les traits se dimensionnent sur la longueur du tableau. QUATRE entrées aujourd'hui —
 * la maquette en montre cinq, « Offres personnalisées » n'est PAS ouverte, l'onglet Offres reste un
 * écran d'attente.
 *
 * PAS d'icône de profil en haut à droite, contrairement à la maquette : le profil a son onglet, et
 * un second point d'entrée au même endroit ne se justifie pas ici.
 *
 * Le score ATS ne figure plus dans ce sommaire, les descriptions étant fixes. Perte assumée : il
 * reste sur « Mon CV », où il s'accompagne de l'action qui le fait bouger.
 */
function CarriereTelephone({
  onOpenMyCv,
  onOpenLinkedin,
  onOpenLetter,
  onOpenInterview,
}: CarriereScreenProps) {
  // Couleurs de la CHARTE, pas celles de la maquette : chaque entité garde la teinte qu'elle porte
  // déjà sur le schéma d'avant connexion, pour qu'on la reconnaisse d'un écran à l'autre.
  const entries: Entry[] = [
    {
      key: 'cv',
      icon: 'file-document-outline',
      title: t('carriere.cv.title'),
      description: t('carriere.cv.description'),
      cta: t('carriere.cv.cta'),
      color: FEATURE_ACCENTS.cv.color,
      onPress: onOpenMyCv,
      testID: 'carriere-cv',
    },
    {
      key: 'linkedin',
      // Le logo LinkedIn reste tel quel : son usage pour signaler l'intégration est couvert par les
      // règles de marque de LinkedIn, dont l'application est partenaire API approuvé.
      icon: 'linkedin',
      title: t('carriere.linkedin.title'),
      description: t('carriere.linkedin.description'),
      cta: t('carriere.linkedin.cta'),
      color: FEATURE_ACCENTS.linkedin.color,
      onPress: onOpenLinkedin,
      testID: 'carriere-linkedin',
    },
    {
      key: 'letter',
      icon: 'email-edit-outline',
      title: t('carriere.letter.title'),
      description: t('carriere.letter.description'),
      cta: t('carriere.letter.cta'),
      color: FEATURE_ACCENTS.letter.color,
      onPress: onOpenLetter,
      testID: 'carriere-letter',
    },
    {
      key: 'interview',
      icon: 'microphone-outline',
      title: t('carriere.interview.title'),
      description: t('carriere.interview.description'),
      cta: t('carriere.interview.cta'),
      color: FEATURE_ACCENTS.interview.color,
      onPress: onOpenInterview,
      testID: 'carriere-interview',
    },
  ];

  // Les dimensions de l'écran, pas des constantes : entre un SE (375 × 667) et un Pro Max
  // (430 × 932) l'écart suffit à décaler l'accrochage et à déséquilibrer la hauteur des cartes.
  const { width, height } = useWindowDimensions();
  const { cardWidth, cardHeight, snap, sideInset } = useMemo(
    () => geometrieCarrousel(width, height),
    [width, height],
  );

  const scrollX = useRef(new Animated.Value(0)).current;

  // Respiration des lueurs : UNE boucle, pilote natif. Fixe à pleine intensité si la personne a
  // demandé de réduire les animations.
  const respiration = useRef(new Animated.Value(1)).current;
  const mouvementReduit = useReduceMotion();
  useEffect(() => {
    if (mouvementReduit) {
      respiration.setValue(1);
      return;
    }
    const boucle = Animated.loop(
      Animated.sequence([
        Animated.timing(respiration, { toValue: 0, duration: LED_RESPIRATION_MS, easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
        Animated.timing(respiration, { toValue: 1, duration: LED_RESPIRATION_MS, easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
      ]),
    );
    boucle.start();
    return () => boucle.stop();
  }, [mouvementReduit, respiration]);
  const rail = useRef<ScrollView | null>(null);

  /**
   * LA SÉRIE, RÉPÉTÉE TROIS FOIS.
   *
   * Les CLÉS RESTENT UNIQUES malgré la duplication — `cv-0`, `cv-1`, `cv-2` — sans quoi React
   * réutiliserait le même nœud pour trois cartes et l'inclinaison de l'une s'appliquerait aux autres.
   *
   * Les `testID` du bloc MÉDIAN restent canoniques (`carriere-cv`), les copies portent un suffixe :
   * une recherche par identifiant continue de désigner une carte et une seule, celle qu'on voit au
   * démarrage.
   */
  /**
   * DROITE À GAUCHE : LE RAIL RESTE PHYSIQUEMENT DE GAUCHE À DROITE, L'ORDRE S'INVERSE.
   *
   * Tout le carrousel raisonne en décalage physique (`contentOffset.x`, bornes des traits), et une
   * ScrollView horizontale ne se comporte pas de la même façon en droite à gauche sur iOS et sur
   * Android. On garde donc le rail de gauche à droite et on inverse l'ordre des cartes : la première
   * fonctionnalité est à droite, la suivante arrive par la gauche. `rang` reste le rang RÉEL.
   */
  const rtl = interfaceRtl();
  const position = (rang: number) => (rtl ? entries.length - 1 - rang : rang);
  const boucle = useMemo(
    () => {
      const sequence = entries.map((entree, rang) => ({ entree, rang }));
      const ordre = rtl ? [...sequence].reverse() : sequence;
      return Array.from({ length: CAROUSEL_REPETITIONS }, (_, bloc) =>
        ordre.map(({ entree, rang }) => ({
          ...entree,
          cle: `${entree.key}-${bloc}`,
          rang,
          testID: bloc === 1 ? entree.testID : `${entree.testID}-r${bloc}`,
        })),
      ).flat();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [width, rtl],
  );

  /** Décalage de la PREMIÈRE fonctionnalité du bloc médian : le point de départ, et le point de retour. */
  const departMedian = snap * (entries.length + position(0));

  /**
   * REPOSITIONNEMENT SILENCIEUX, À LA FIN DE L'ÉLAN.
   *
   * Pendant le geste, toucher au décalage produirait une saccade visible sous le doigt. On attend
   * donc l'arrêt : si l'on a dérivé dans le bloc de gauche ou celui de droite, on saute d'un bloc
   * entier, sans animation. Le décalage change, la carte affichée non — l'œil ne voit rien.
   *
   * L'INTERPOLATION SURVIT au saut : les bornes de chaque carte sont calculées sur son index ABSOLU
   * dans la liste triplée, et `scrollX` suit le décalage réel. Après le saut, c'est une autre carte
   * qui occupe le centre, avec ses propres bornes — l'inclinaison et l'échelle restent justes.
   */
  const surFinElan = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const cible = recentrer(e.nativeEvent.contentOffset.x, snap * entries.length);
    if (cible !== null) rail.current?.scrollTo({ x: cible, animated: false });
  };

  /**
   * L'INDEX ACTIF EST UN ÉTAT, l'apparence est une ANIMATION — les deux ne servent pas à la même
   * chose et ne se mettent pas à jour au même rythme.
   *
   * Les traits suivent le doigt en continu par interpolation de `scrollX`, sur le pilote natif :
   * c'est ce qui les fait bouger PENDANT le geste et pas seulement à son terme. Cet état-ci ne
   * change qu'au franchissement d'une carte ; il ne sert qu'à ce qui est discret par nature :
   * l'annonce VoiceOver de l'élément sélectionné.
   */
  const [active, setActive] = useState(0);
  const surDefilement = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const brut = Math.round(e.nativeEvent.contentOffset.x / snap);
    // MODULO, jamais l'index brut : sur la liste triplée, la neuvième carte est la première. Le
    // trait de progression doit désigner la FEATURE, pas la position dans le rail.
    const reel = position(((brut % entries.length) + entries.length) % entries.length);
    setActive((precedent) => (precedent === reel ? precedent : reel));
  };

  /**
   * LES DEUX BLOCS DE L'ACCROCHE, extraits pour pouvoir être enveloppés — ou pas — selon la
   * plateforme, sans dupliquer le balisage.
   *
   * Le libellé porte une coupure en dur (`\n`) qui impose deux lignes : elle sert la maquette
   * mobile et dessert une fenêtre large, où l'accroche tient d'un seul tenant. Elle est donc
   * défaite sur le web, et c'est la rangée qui décide des retours à la ligne.
   */
  // 34 pt sur un iPhone courant, un peu moins sur un petit écran : « Deviens le candidat » doit tenir
  // sur une ligne, la coupure est portée par le libellé.
  const tailleTitre = Math.min(34, Math.round(width * 0.087));
  const styleTitre = [
    styles.title,
    { fontSize: tailleTitre, lineHeight: tailleTitre + 4 },
    ...(SUR_LE_WEB ? [styles.titreWeb] : []),
  ];
  const accroche = (
    <>
      <Text style={styleTitre}>
        {SUR_LE_WEB ? t('carriere.headline').replace('\n', ' ') : t('carriere.headline')}
      </Text>
      <GradientText testID="carriere-headline-gradient" style={styleTitre}>
        {t('carriere.headlineAccent')}
      </GradientText>
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <FondAube />

      <ScrollView
        testID="carriere-page"
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        {/* BANNIÈRE — repère de marque en tête de page, sur le web seulement.
            Le mobile n'en a pas besoin : l'onglet actif et la barre du bas disent déjà où l'on est,
            alors qu'un navigateur n'a que l'onglet système et une URL. */}
        {SUR_LE_WEB ? (
          <View style={styles.banniere}>
            <BrandLogo variant="wordmark" width={168} testID="carriere-brand" />
          </View>
        ) : null}

        {/* Accroche en DEUX blocs, motif repris de l'onboarding : le dégradé de marque se pose sur
            un bloc entier (GradientText masque un dégradé par le texte), jamais au milieu d'une
            ligne. Le dernier mot vit donc sur sa propre ligne — c'est ce que montre la maquette.

            SUR LE WEB, la coupure en dur du libellé est défaite et les deux blocs passent sur une
            RANGÉE centrée : une accroche calée à gauche sur trois lignes, au milieu d'une fenêtre
            large, se lit comme un reste de téléphone étiré. `flexWrap` la laisse revenir à deux
            lignes dès que la fenêtre se resserre — elle ne déborde donc jamais.

            L'enveloppe n'existe QUE sur le web : sur mobile l'arbre rendu est exactement celui
            d'avant, sans conteneur supplémentaire. */}
        {SUR_LE_WEB ? (
          <View style={styles.titreRangee}>{accroche}</View>
        ) : (
          accroche
        )}

        <Animated.ScrollView
          testID="carriere-carousel"
          ref={rail as never}
          horizontal
          // Départ au centre du bloc médian, SANS animation : on doit pouvoir aller à gauche dès la
          // première image. `contentOffset` sert iOS, `scrollTo` au montage rattrape Android, où
          // cette prop n'est pas honorée de façon fiable.
          contentOffset={{ x: departMedian, y: 0 }}
          onLayout={() => rail.current?.scrollTo({ x: departMedian, animated: false })}
          onMomentumScrollEnd={surFinElan}
          showsHorizontalScrollIndicator={false}
          snapToInterval={snap}
          decelerationRate="fast"
          // Sans cela une impulsion un peu vive traverse deux ou trois cartes d'un coup : on ne sait
          // plus où on a atterri, et les traits deviennent illisibles pendant le trajet.
          disableIntervalMomentum
          scrollEventThrottle={16}
          onScroll={Animated.event([{ nativeEvent: { contentOffset: { x: scrollX } } }], {
            useNativeDriver: true,
            listener: surDefilement,
          })}
          style={[styles.carousel, BLOC_LTR]}
          {...PROPS_LTR}
          contentContainerStyle={{ paddingHorizontal: sideInset, paddingBottom: LED_HAUTEUR }}
        >
          {boucle.map(({ key, cle, rang, ...entry }, index) => (
            <EntryCard
              key={cle}
              entry={entry}
              index={index}
              total={entries.length}
              position={rang + 1}
              scrollX={scrollX}
              snap={snap}
              cardWidth={cardWidth}
              cardHeight={cardHeight}
              active={rang === active}
              respiration={respiration}
            />
          ))}
        </Animated.ScrollView>

        {/* VIDE ÉLASTIQUE. Ce qui manquait n'était pas de la marge sous les traits mais de l'air
            sous les CARTES : une marge fixe donnerait le bon écart sur un seul appareil, et la
            hauteur libre n'est pas la même sur un SE que sur un Pro Max. Cet espace prend ce qui
            reste, donc les traits descendent près de la barre d'onglets partout. */}
        <View testID="carriere-slack" style={styles.slack} />

        <View testID="carriere-indicators" style={styles.indicators}>
          {entries.map((entry, index) => (
            <Indicator
              key={entry.key}
              testID={`carriere-indicator-${entry.key}`}
              index={position(index)}
              snap={snap}
              scrollX={scrollX}
              selected={index === active}
              total={entries.length}
            />
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

/**
 * Point d'entrée de l'onglet Carrière. Téléphone (et tout appareil natif, toujours « compact ») :
 * le carrousel, inchangé. Navigateur à partir de 600 px : un tableau de bord conçu pour la souris.
 */
export function CarriereScreen(props: CarriereScreenProps) {
  const { large } = useGabarit();
  return large ? <CarriereLarge {...props} /> : <CarriereTelephone {...props} />;
}

/**
 * Un trait par carte.
 *
 * LARGEUR ANIMÉE PAR `scaleX`, PAS PAR `width` : `width` n'est pas transmissible au pilote natif, et
 * le trait resterait alors en arrière du doigt sur un appareil chargé — précisément le défaut qu'on
 * cherche à éviter. Le trait est donc dessiné à sa taille active et comprimé au repos.
 *
 * Le dégradé est posé en permanence sous une opacité animée, plutôt que monté et démonté au
 * franchissement : un montage à chaque carte franchie ferait clignoter le trait.
 */
function Indicator({
  testID,
  index,
  snap,
  scrollX,
  selected,
  total,
}: {
  testID: string;
  index: number;
  snap: number;
  scrollX: Animated.Value;
  selected: boolean;
  total: number;
}) {
  const { points, centres } = bornesIndicateur(index, snap, total);
  const compression = INDICATOR_IDLE_WIDTH / INDICATOR_ACTIVE_WIDTH;
  // Le point haut est reconnu par sa VALEUR, pas par sa position dans le tableau : un point
  // dédoublonné décalerait un motif fondé sur l'indice, et le trait s'allumerait à côté.
  const cycle = <T,>(bas: T, haut: T): T[] => points.map((p) => (centres.has(p) ? haut : bas));
  const scaleX = scrollX.interpolate({
    inputRange: points,
    outputRange: cycle(compression, 1),
    extrapolate: 'clamp',
  });
  const opacity = scrollX.interpolate({
    inputRange: points,
    outputRange: cycle(0, 1),
    extrapolate: 'clamp',
  });

  return (
    <Animated.View
      testID={testID}
      accessibilityState={{ selected }}
      style={[styles.indicator, { transform: [{ scaleX }] }]}
    >
      <Animated.View style={[StyleSheet.absoluteFill, { opacity }]}>
        <LinearGradient
          testID={`${testID}-gradient`}
          colors={gradients.brand}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={StyleSheet.absoluteFill}
        />
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: {
    padding: screenPadding,
    paddingTop: spacing.xxl * 2,
    // L'AIR SOUS LES TRAITS : la barre d'onglets est un frère du conteneur d'écrans et consomme
    // déjà l'inset de l'appareil, ce rembourrage est donc exactement l'écart entre le dernier trait
    // et la barre. Sans lui, l'espace élastique collerait les traits dessus.
    paddingBottom: spacing.xxl,
    // Sans `flexGrow`, le conteneur se contente de la hauteur de son contenu et l'espace élastique
    // n'a rien à prendre : les traits resteraient collés sous le carrousel.
    flexGrow: 1,
  },

  // BANNIÈRE (web) : centrée, et séparée de l'accroche par une respiration franche.
  banniere: { alignItems: 'center', marginBottom: spacing.xl },

  /**
   * RANGÉE DE L'ACCROCHE (web) : les deux blocs côte à côte, centrés, bornés à la largeur de
   * lecture. `flexWrap` est ce qui empêche le débordement en fenêtre étroite — l'accroche revient
   * alors à deux lignes plutôt que de sortir du cadre.
   */
  titreRangee: {
    ...CONTENU_BORNE,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  titreWeb: { textAlign: 'center' },

  title: {
    fontWeight: '800',
    letterSpacing: -1,
    color: colors.navy,
  },

  // Marge négative : le carrousel doit traverser toute la largeur pour que les voisines dépassent
  // jusqu'au bord, alors que le reste du contenu vit dans le rembourrage d'écran.
  // La marge HAUTE est délibérément plus généreuse que l'ancienne liste : le titre a besoin d'air
  // avant que les cartes ne commencent.
  carousel: {
    marginHorizontal: -screenPadding,
    marginTop: spacing.xxl + spacing.md,
    // La mise à l'échelle déborde du flux : sans quoi l'ombre des cartes est rognée en haut.
    overflow: 'visible',
    // La lueur des cartes dépasse sous leur bord bas : on réserve sa hauteur dans le rail (les
    // ScrollView rognent leur contenu) et on la reprend en marge, pour que la mise en page ne bouge pas.
    marginBottom: -LED_HAUTEUR,
  },
  // Demi-recouvrement de chaque côté : deux voisines mises bout à bout se chevauchent donc d'un
  // CARD_OVERLAP entier.
  slot: { marginHorizontal: -CARD_OVERLAP / 2 },
  card: {
    padding: spacing.lg,
    borderRadius: radius.xl,
    alignItems: 'center',
    // Le corps occupe l'espace et se centre ; le bouton reste posé en bas, à la même hauteur d'une
    // carte à l'autre quelle que soit la longueur des textes.
    justifyContent: 'space-between',
    ...shadow.card,
  },
  body: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing.sm },
  // Le rail reste de gauche à droite (voir `ordre`) ; le texte des cartes, lui, suit l'interface.
  corpsRtl: { direction: 'rtl' },
  // AUCUN fond posé ici : la forme n'est plus un aplat, elle est dessinée par le halo qui s'éteint
  // avant son bord. `overflow: visible` laisse les étincelles déborder.
  // Collée sous la carte, pleine largeur du slot. Le `zIndex` négatif n'est pas utile : elle précède
  // la carte dans l'arbre, donc passe dessous.
  // Ancrée au bord bas de la carte : la partie haute (deux rayons) passe derrière elle, le reste dépasse dessous.
  led: { position: 'absolute', left: 0, right: 0, bottom: -LED_HAUTEUR, height: LED_HAUTEUR + radius.xl * 2 },
  verre: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: GLYPH_RADIUS,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.38)',
  },
  glyphBox: {
    width: GLYPH_BOX,
    height: GLYPH_BOX,
    borderRadius: GLYPH_RADIUS,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
    marginBottom: spacing.md,
  },
  sparkles: { ...StyleSheet.absoluteFillObject },
  // Coin supérieur droit, légèrement DÉBORDANTES : positions relevées sur la maquette, rapportées
  // à la boîte du halo.
  sparkleMain: { position: 'absolute', top: -spacing.sm, end: -spacing.md },
  sparkleSmall: {
    position: 'absolute',
    top: spacing.md,
    end: -spacing.xl,
    opacity: SPARKLE_FAINT_OPACITY,
  },
  cardTitle: {
    fontSize: typography.h1.size,
    fontWeight: typography.h1.weight,
    lineHeight: typography.h1.lineHeight,
    color: colors.white,
    textAlign: 'center',
  },
  cardDescription: {
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: TEXT_ON_COLOR_SOFT,
    textAlign: 'center',
  },
  cta: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    marginTop: spacing.lg,
    paddingVertical: spacing.md,
    // `md` et non `lg` : sur une carte de 217 pt (iPhone SE), « Améliorer mon profil » et sa flèche
    // n'entrent plus dans un rembourrage large.
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
  },
  ctaText: { fontSize: typography.bodyMd.size, fontWeight: typography.h3.weight },

  /**
   * PLANCHER, PAS DE PLAFOND — l'inverse du `slack` de l'onboarding.
   *
   * Là-bas l'élastique est plafonné parce que repousser l'accroche trop bas la décrocherait du
   * reste. Ici, descendre les traits vers la barre d'onglets EST l'objectif : plus l'écran est
   * grand, plus l'écart doit grandir. Le plancher garde un écart visible le jour où le contenu
   * remplit la hauteur — sur SE avec une police agrandie, par exemple.
   */
  slack: { flexGrow: 1, minHeight: spacing.xxl },
  // Pas de `marginTop` : l'écart au carrousel est donné par l'espace élastique. Les deux se
  // cumuleraient.
  indicators: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.sm,
  },
  indicator: {
    width: INDICATOR_ACTIVE_WIDTH,
    height: ACCENT_HEIGHT,
    borderRadius: radius.pill,
    backgroundColor: colors.grey200,
    overflow: 'hidden',
  },
});
